import { useLoaderData, useRevalidator, type LoaderFunctionArgs } from "react-router";
import { Lines, Loading, SettingsRowsSkeleton } from "../../components/skeletons.jsx";
import { Deferred, EmptyState } from "../../components/states.jsx";
import { useI18n } from "../../i18n/index.js";
import { api } from "../../lib/api.js";
import { deferred } from "../../lib/deferred.js";
import { apiContext } from "../../lib/session.js";
import { workspaceContext } from "../../lib/workspace.js";
import { Button, ConfirmDialog, PageHeader, SettingsBlock, SettingsRow, SettingsSection, TerminalIcon, useToast, Skeleton } from "../../ui/index.js";

// Coding agents the signed-in person connected to this workspace over MCP (D-0021). Each acts as them, here only;
// nobody sees or ends anyone else's.
export function agentsLoader({ context }: LoaderFunctionArgs) {
  const ws = context.get(workspaceContext);
  return { workspaceId: ws.id, connections: deferred(context.get(apiContext).workspace(ws.id).listAgentConnections()) };
}

/** How to connect, then who is connected. */
function ConnectionsSkeleton() {
  const { t } = useI18n();
  return (
    <Loading>
      <SettingsSection title={t("agents.connect.title")}>
        <SettingsBlock>
          <Lines count={4} />
        </SettingsBlock>
      </SettingsSection>
      <SettingsSection title={t("agents.connected.title")}>
        <SettingsRowsSkeleton count={2} control={<Skeleton shape="button-sm" width={88} />} />
      </SettingsSection>
    </Loading>
  );
}

export function AgentsSkeleton() {
  const { t } = useI18n();
  return (
    <>
      <PageHeader title={t("agents.title")} description={t("agents.description")} />
      <ConnectionsSkeleton />
    </>
  );
}

export function AgentsRoute() {
  const { workspaceId, connections } = useLoaderData<typeof agentsLoader>();
  const revalidator = useRevalidator();
  const { t, formatDate } = useI18n();
  const toast = useToast();

  return (
    <>
      <PageHeader title={t("agents.title")} description={t("agents.description")} />
      <Deferred data={connections} fallback={<ConnectionsSkeleton />}>
        {({ mcpUrl, items }) => (
          <>
            <SettingsSection title={t("agents.connect.title")}>
              <SettingsBlock>
                <pre className="dt-mono" style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                  {`# Claude Code\nclaude mcp add --transport http diotia ${mcpUrl}\n\n# Codex\ncodex mcp add diotia --url ${mcpUrl}\ncodex mcp login diotia`}
                </pre>
              </SettingsBlock>
            </SettingsSection>
            <SettingsSection title={t("agents.connected.title")}>
              {items.length === 0 ? (
                <SettingsBlock>
                  <EmptyState title={t("agents.empty.title")} hint={t("agents.empty.hint")} icon={<TerminalIcon size={18} />} />
                </SettingsBlock>
              ) : (
                items.map((c) => (
                  <SettingsRow key={c.id} label={c.name} description={t("agents.connectedAt", { date: formatDate(c.connectedAt) })}>
                    <ConfirmDialog
                      trigger={
                        <Button variant="ghost" size="sm">
                          {t("agents.disconnect")}
                        </Button>
                      }
                      title={t("agents.disconnect.title", { name: c.name })}
                      description={t("agents.disconnect.description")}
                      confirmLabel={t("agents.disconnect.confirm")}
                      destructive
                      onConfirm={async () => {
                        await api.workspace(workspaceId).disconnectAgent(c.id);
                        toast.neutral(t("agents.disconnected"), c.name);
                        revalidator.revalidate();
                      }}
                    />
                  </SettingsRow>
                ))
              )}
            </SettingsSection>
          </>
        )}
      </Deferred>
    </>
  );
}
